import type { LucideIcon } from 'lucide-react';
import { Castle, Dice6, MessageCircle, Bird, Map, Smartphone, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EndpointType } from '@/types/endpoint';

/**
 * Icon configuration for endpoint types
 */
const ICON_MAP: Record<EndpointType, LucideIcon> = {
  foundry: Castle,
  roll20: Dice6,
  discord: MessageCircle,
  owlbear: Bird,
  dndbeyond: Map,
  standalone: Smartphone,
  player: User
};

/**
 * Color configuration for endpoint types
 */
const COLOR_MAP: Record<EndpointType, string> = {
  foundry: 'text-lime-500',
  roll20: 'text-orange-400',
  discord: 'text-indigo-400',
  owlbear: 'text-teal-400',
  dndbeyond: 'text-red-400',
  standalone: 'text-green-400',
  player: 'text-blue-400'
};

export interface EndpointTypeIconProps {
  /**
   * The endpoint type to display
   */
  type: EndpointType;

  /**
   * Optional additional CSS classes
   */
  className?: string;

  /**
   * Icon size in pixels
   * @default 24
   */
  size?: number;
}

/**
 * Displays a color-coded icon for an endpoint type
 *
 * Maps endpoint types to lucide-react icons with color coding:
 * - Foundry VTT: Castle icon (lime green)
 * - Roll20: Dice6 icon (orange)
 * - Discord: MessageCircle icon (indigo)
 * - Owlbear Rodeo: Bird icon (teal)
 * - D&D Beyond Maps: Map icon (red)
 *
 * @example
 * ```tsx
 * <EndpointTypeIcon type="foundry" size={32} />
 * <EndpointTypeIcon type="roll20" className="mr-2" />
 * ```
 */
export function EndpointTypeIcon({
  type,
  className,
  size = 24
}: EndpointTypeIconProps) {
  const IconComponent = ICON_MAP[type];
  const colorClass = COLOR_MAP[type];

  return (
    <IconComponent className={cn(colorClass, className)} width={size} height={size} aria-label={`${type} endpoint`} />
  );
}
